// 範囲指定（マーキー）選択のための純粋な幾何ヘルパー。
// ドラッグの始点・終点（ステージ座標）から正規化した矩形を作り、その矩形に
// 重なるオブジェクト ID を求める。DOM やストアには依存しないため単体テストしやすい。

import { isAabbContained, type CanvasObjectType } from "@refinement-canvas/domain";
import { aabbIntersects, type Aabb } from "./culling";

/** 範囲指定枠（ステージ座標・幅高さは常に非負）。 */
export interface MarqueeRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * ドラッグ始点 (x0,y0) と現在点 (x1,y1) から、左上原点・非負サイズの矩形へ正規化する。
 * どの向きにドラッグしても同じ矩形として扱えるようにする。
 */
export function normalizeMarqueeRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number
): MarqueeRect {
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1),
    w: Math.abs(x1 - x0),
    h: Math.abs(y1 - y0),
  };
}

/**
 * 範囲指定枠に AABB が重なる（辺が接する場合も含む）オブジェクトの ID を返す。
 * 回転は考慮せず、各オブジェクトの x/y/w/h（未回転 AABB）で交差判定する。
 * 描画順とは無関係に Object.entries の列挙順で返す。
 *
 * 例外（セクション）: セクションは大型の背景コンテナで、重なり判定だと「セクション内の
 * 付箋を囲うだけのつもりが背後のセクションごと選択され、移動で内容物まで巻き込まれる」
 * 事故が起きるため、**マーキー矩形に完全に包含される場合のみ**選択対象にする。
 */
export function objectIdsInMarquee<
  T extends Aabb & { readonly type?: CanvasObjectType | undefined },
>(
  objects: Record<string, T>,
  rect: MarqueeRect
): string[] {
  const ids: string[] = [];
  for (const [id, object] of Object.entries(objects)) {
    const box = { x: object.x, y: object.y, w: object.w, h: object.h };
    if (object.type === "section") {
      if (isAabbContained(box, rect)) ids.push(id);
      continue;
    }
    if (aabbIntersects(rect, box)) {
      ids.push(id);
    }
  }
  return ids;
}
